import { clsx } from "clsx";
import { type FC, useContext, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { type PanelApi, PanelGroup, PanelOrientation, PanelPosition } from "widgets";
import { useHotKeys, useURLSearchParam, useURLSearchParamsList } from "widgets/hooks";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { getGenomeViewerPageTips } from "@/app-utils/help.js";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { rangeEquals } from "@/app-utils/range.ts";
import { FullscreenContext } from "@/app_context.ts";
import { type JobResult, type Organism, type Range, type VariantAnalysisGroup } from "@/graphql.ts";
import TipTour from "../core/TipTour.js";
import { ViewGenomeViewerLayoutButtons } from "../core/ViewPaneLayoutButtons.js";
import { GenomeBrowserButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import RefGenomeSelector from "../entities/RefGenomeSelector.js";
import { GenomeViewer } from "./GenomeViewer.js";
import GenomicFeatureTable from "./genomic-table/GenomicFeatureTable.js";
import { useUpdateGenomeViewerTracks } from "./hooks/useSequences.js";
import {
  type IGenomeBrowserState,
  type ISortableTrack,
  clearGenomeBrowserData,
  clearGenomeBrowserSelection,
  featuresTableHideStateChanged,
  initGenomeBrowser,
  resetGenomeBrowser,
  trackHeaderWidthChanged,
  viewRangeChanged,
} from "@/redux/modules/genome-browser.ts";
import { loadView } from "@/redux/modules/saved-view.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_HEADER_DEFAULT_WIDTH, TRACK_HEADER_MIN_WIDTH } from "@/settings/genome-viewer-settings.ts";
import { Hotkeys } from "@/settings/hotkeys.ts";
import { TRACK_TYPE, UNKNOWN_GENOME_ID, VIEW } from "@/settings/settings.js";

export interface IGenomeViewOwnProps {
  title?: string;
  subtitle?: string;
  genomeId?: number; // genome to be viewed
  contigId?: number; // contig to be initially shown in browser
  range?: Range; // range in bp to be initially shown in browser
  defaultTracks: ISortableTrack[]; // tracks to be initially shown
  genomes?: Organism[]; // to show only the list of mutant genomes in the genome picker e.g. for Variant's genome browser
  jobResult?: JobResult; // BLAST results for now
  selectionTooltipText?: string; // text to be displayed in the tooltip if a genome needs to be selected first
  attributesWidgets?: JSX.Element[];
  headerTags?: JSX.Element[];
}

/**
 * A visualization component containing the genome browser on the left and the genomic feature table on the right.
 */
const GenomeView: FC<IGenomeViewOwnProps> = (props) => {
  const { genomeId, range, contigId } = props;
  const dispatch = useDispatch<ReduxDispatch>();
  const apiRef = useRef<PanelApi>(null);

  // keep the previous value of range in order to be able to compare it with the new value
  const previousRange = useRef<Range>();

  const isLabelsHidden = useSelector<IAppState, boolean>(
    (state) => state.genomeBrowser.layout.headerWidth === TRACK_HEADER_MIN_WIDTH,
    shallowEqual,
  );
  const pageRef = useRef<HTMLDivElement>(null);
  useHotKeys(
    Hotkeys.genomeViewer.toggleHeader,
    () => dispatch(trackHeaderWidthChanged(isLabelsHidden ? TRACK_HEADER_DEFAULT_WIDTH : TRACK_HEADER_MIN_WIDTH)),
    pageRef,
  );
  const [isFullscreen, setFullscreen] = useContext(FullscreenContext);

  const { featuresTableLayout, genome, contig, tracks, activeFeaturesTable } = useSelector<
    IAppState,
    IGenomeBrowserState
  >((state) => state.genomeBrowser, shallowEqual);

  useHotKeys(Hotkeys.genomeViewer.clearSelection, () => {
    dispatch(clearGenomeBrowserSelection());
  });

  const viewId = useURLViewID();
  const genomeViewerFileIds = useURLSearchParamsList("bam_files") || useURLSearchParamsList("bed_files");
  useUpdateGenomeViewerTracks(props.defaultTracks);

  const rangeFromUrl = useURLSearchParamsList("range");
  const contigFromUrl = useURLSearchParam("contig");
  const showGrid = useURLSearchParam("showGrid");

  // happens only at init time
  useEffect(() => {
    if (showGrid) {
      dispatch(
        featuresTableHideStateChanged({
          ...featuresTableLayout,
          hidden: false,
        }),
      );
    }
  }, []);

  const [showToolbars, setShowToolbars] = useState(true);

  useEffect(() => {
    const resetFullscreen = () => {
      if (!document.fullscreenElement) setFullscreen(false);
    };

    if (isFullscreen && !document.fullscreenElement) {
      void document.body.requestFullscreen().catch(() => setFullscreen(false));
    } else if (document.fullscreenElement) {
      void document.exitFullscreen();
    }

    document.addEventListener("fullscreenchange", resetFullscreen);

    return () => {
      // If this toolbar is unmounted while on fullscreen, it will probably mean we should quit the fullscreen mode.
      // Changing page in the Navigation Panel is impossible in fullscreen mode as it's hidden.
      if (document.fullscreenElement) {
        document.exitFullscreen().then(() => setFullscreen(false));
      }
      document.removeEventListener("fullscreenchange", resetFullscreen);
    };
  }, [isFullscreen, setFullscreen]);

  useEffect(() => {
    const init = () => {
      if ((genomeId && genomeId !== UNKNOWN_GENOME_ID) || genomeViewerFileIds || contigId) {
        const overwrittenRange: Range | undefined = rangeFromUrl
          ? { start: Number(rangeFromUrl[0]), stop: Number(rangeFromUrl[1]) }
          : range;
        const overwrittenContigId: number = contigFromUrl ? Number(contigFromUrl) : contigId;

        dispatch(
          // Will load all the tracks. Visible tracks and default contig/range may be overwritten by url params
          // As initGenomeBrowser will load all the tracks, we can't work with defaultTracks only as some "dynamic" tracks are missing.
          initGenomeBrowser(genomeId || UNKNOWN_GENOME_ID, overwrittenContigId, overwrittenRange),
        );
      } else {
        dispatch(clearGenomeBrowserData());
      }
    };

    if (viewId == undefined) {
      // loads as there is no view.
      init();
    } else {
      dispatch(loadView(viewId)).then((view) => {
        if (view?.viewType !== VIEW.GENOME_VIEW.type) {
          // issue with viewID (doesn't exist...), so loads as if no views
          init();
        }
      });
    }

    return function cleanup() {
      dispatch(clearGenomeBrowserData());
    };
  }, [genomeId, contigId]);

  useEffect(() => {
    return () => {
      dispatch(resetGenomeBrowser());
    };
  }, []);

  useEffect(() => {
    // case where the genome and contig stay the same but the defaultRange has changed (e.g. when paginating through entities)
    // FIXME this does not work any longer since we switched to hooks: this should be a case where no genome init() is needed
    if (previousRange.current == undefined || !rangeEquals(range, previousRange.current)) {
      dispatch(viewRangeChanged(range));
    }

    previousRange.current = range;
  }, [range]);

  useEffect(() => {
    if (featuresTableLayout.hidden) {
      apiRef.current?.close();
    } else {
      apiRef.current?.open?.();
    }
  }, [featuresTableLayout.hidden, featuresTableLayout.verticalLayout]);

  return (
    <TipTour tips={getGenomeViewerPageTips()} disable={!genome}>
      <PageHeader
        title={props.title || "Genome View"}
        subtitle={props.subtitle}
        widgets={props.attributesWidgets}
        tags={props.headerTags}
        toolsWidgets={
          contig
            ? [
                <ViewGenomeViewerLayoutButtons
                  toolbarsState={[showToolbars, setShowToolbars]}
                  key={"genome-view-action-1"}
                />,
                <MaybeRefGenomeSelector genome={genome} tracks={tracks} key={"genome-view-action-4"} />,
              ]
            : undefined
        }
        actionWidgets={contig ? [<GenomeBrowserButtonHeader key={"genome-view-action-5"} />] : undefined}
      />
      <div className={styleLayout.page} ref={pageRef} tabIndex={0}>
        <PanelGroup
          apiRef={apiRef}
          orientation={featuresTableLayout.verticalLayout ? PanelOrientation.Vertical : PanelOrientation.Horizontal}
          defaultSize={featuresTableLayout.size}
          minSize={featuresTableLayout.verticalLayout ? 195 : 512}
          onMoveEnd={(size, _, isClosed) => {
            dispatch(
              featuresTableHideStateChanged({
                ...featuresTableLayout,
                hidden: isClosed,
                size,
              }),
            );
          }}
          fixedPanel={PanelPosition.Trailing}
        >
          <div className={clsx(styleLayout.grid_pane)}>
            <GenomeViewer
              showToolbars={showToolbars}
              startupEmpty={props.genomeId === UNKNOWN_GENOME_ID && !genomeViewerFileIds?.length && !viewId} // The view will load the data later, so it's not empty
              jobResult={props.jobResult}
              genomes={props.genomes}
              selectionTooltipText={props.selectionTooltipText}
            />
          </div>
          {genome && (
            <div className={clsx(styleLayout.grid_pane)}>
              <GenomicFeatureTable openedFromUrlParam={!!showGrid} />
            </div>
          )}
        </PanelGroup>
      </div>
    </TipTour>
  );
};

const MaybeRefGenomeSelector: FC<{ genome: Organism; tracks: ISortableTrack[]; isUiSmall?: boolean }> = (props) => {
  if (props.genome) {
    const variantTrackAvailable = props.tracks.some((track) => !track.hidden && track.type === TRACK_TYPE.VARIANT);
    if (variantTrackAvailable) {
      const analysisGroup: VariantAnalysisGroup = props.genome.variantAnalysisGroups?.[0]; // derived variants have only one single analysis group
      const referenceGenomes = analysisGroup.refGenomes;
      // If referenceGenomes == null, do not show the Ref Genome Select => nothing to select
      if (referenceGenomes)
        return <RefGenomeSelector referenceGenomes={referenceGenomes} key={"genome-view-action-99"} />;
    }
  }

  return null;
};

export { GenomeView };
